import { gql } from '@apollo/client';
import { graphql, withApollo } from '@apollo/client/react/hoc';
import update from 'immutability-helper';
import { flowRight } from 'lodash-es';
import { UserRole, heapTrackEvent, isMember } from '@marvelapp/core';
import { showGenericServerError } from '../ToastContainer';
import { getTotalExpansionCost } from './InviteUsers';
import InviteUsersModal from './InviteUsersModal';
const GET_SEAT_ALLOWANCE = gql `
  query getSeatAllowance {
    user {
      pk
      company {
        pk
        billing {
          subscriptionPk
          billingCycle
          payment
          currency
          seats {
            payingFor
            used
            limit
            expandable
            originallyPurchased
          }
        }
        guestCount
      }
    }
  }
`;
const INVITE_USERS_TO_WORKSPACE = gql `
  mutation INVITE_USERS_TO_WORKSPACE(
    $emails: [String]!
    $role: RoleEnum
    $isBallpark: Boolean
  ) {
    inviteUsersToWorkspace(
      input: { emails: $emails, role: $role, isBallpark: $isBallpark }
    ) {
      succeeded
      failed {
        email
        code
      }
      error {
        message
        code
      }
    }
  }
`;
export default flowRight(withApollo, graphql(GET_SEAT_ALLOWANCE, {
    skip: (props) => ![UserRole.Owner, UserRole.Admin, UserRole.Editor].some((role) => role === props.user.role),
    props({ data: { loading, user } }) {
        return {
            billingCycle: user === null || user === void 0 ? void 0 : user.company.billing.billingCycle,
            pricePerUser: getPricePerUser(user === null || user === void 0 ? void 0 : user.company.billing),
            seatsLimit: user === null || user === void 0 ? void 0 : user.company.billing.seats.limit,
            seatsExpandable: user === null || user === void 0 ? void 0 : user.company.billing.seats.expandable,
            seatsUsed: user === null || user === void 0 ? void 0 : user.company.billing.seats.used,
            seatsOriginallyPurchased: user === null || user === void 0 ? void 0 : user.company.billing.seats.originallyPurchased,
            isFetchingQuantity: loading,
            guestCount: user === null || user === void 0 ? void 0 : user.company.guestCount,
            currency: user === null || user === void 0 ? void 0 : user.company.billing.currency,
        };
    },
}), graphql(INVITE_USERS_TO_WORKSPACE, {
    props({ mutate, ownProps: { client, currency, isBallpark = false, onInviteSuccess, planLabel, pricePerUser, seatsOriginallyPurchased, seatsUsed, }, }) {
        const inviteUsers = ({ setEmails, setIsSendingInvite, setShowFormError, usersToInvite, }) => {
            const retry = () => {
                setIsSendingInvite(true);
                setShowFormError(false);
                inviteUsers({
                    setEmails,
                    setIsSendingInvite,
                    setShowFormError,
                    usersToInvite,
                });
            };
            return mutate({ variables: Object.assign(Object.assign({}, usersToInvite), { isBallpark }) })
                .then(({ data }) => {
                const { inviteUsersToWorkspace } = data;
                if (usersToInvite.role !== UserRole.ExternalReviewer) {
                    const invitedMembersCount = inviteUsersToWorkspace.succeeded.length;
                    updateSeatsUsedStoredInCache(client, invitedMembersCount);
                }
                const invitedUsersCount = inviteUsersToWorkspace.succeeded.length;
                const pendingTotalMembers = invitedUsersCount + seatsUsed;
                trackInviteUsers({
                    planLabel,
                    seatsUsedBeforeInvite: seatsUsed,
                    seatsOriginallyPurchased,
                    invitedUsersCount,
                    role: usersToInvite.role,
                    currency,
                    totalExpansion: isMember({ role: usersToInvite.role }) &&
                        pendingTotalMembers > seatsOriginallyPurchased
                        ? getTotalExpansionCost({
                            pendingTotalMembers,
                            seatsOriginallyPurchased,
                            pricePerUser,
                            seatsUsed,
                        })
                        : 0,
                });
                onInviteSuccess({
                    inviteUsersToWorkspace,
                    retry,
                    setEmails,
                    setIsSendingInvite,
                    setShowFormError,
                });
            })
                .catch(() => {
                setIsSendingInvite(false);
                showGenericServerError({ retry, closeOnClick: true });
            });
        };
        return {
            inviteUsers,
        };
    },
}))(InviteUsersModal);
function updateSeatsUsedStoredInCache(client, increment) {
    const cachedData = client.readQuery({ query: GET_SEAT_ALLOWANCE });
    client.writeQuery({
        query: GET_SEAT_ALLOWANCE,
        data: update(cachedData, {
            user: {
                company: {
                    billing: { seats: { used: { $apply: (old) => old + increment } } },
                },
            },
        }),
    });
}
function getPricePerUser(billingInfo) {
    if (billingInfo) {
        const { seats, payment } = billingInfo;
        return Math.ceil(payment / 100) / seats.payingFor;
    }
}
function trackInviteUsers(inviteDetails) {
    heapTrackEvent('Invited users', inviteDetails);
}
export { showAddPeopleNotifications, sortFailedInvitesByReason, } from './inviteUsersNotifications';
